import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SiteNav, SiteFooter, PageHero } from "@/components/SiteChrome";

const GALLERY = [
  { img: "/defense/m2-bradley-sprocket.png", tag: "Army · #6781", title: "M2 Bradley Sprocket", big: true },
  { img: "/defense/navy-bevel-gearbox.png", tag: "Navy · #4481", title: "Bevel Gear Box" },
  { img: "/defense/army-gun-mount.png", tag: "Army", title: "Gun Mount" },
  { img: "/defense/aerospace-gear-mechanism.png", tag: "Aerospace", title: "Gear Mechanism" },
  { img: "/defense/planetary-gearbox.png", tag: "Strategic", title: "Planetary Gearbox", big: true },
  { img: "/defense/armoured-vehicle-a.png", tag: "Armoured Vehicles", title: "Vehicle Assembly" },
  { img: "/defense/armoured-vehicle-b.png", tag: "Armoured Vehicles", title: "Drive Components" },
  { img: "/defense/hydraulic-cylinder.jpg", tag: "Strategic", title: "Hydraulic Cylinder" },
  { img: "/defense/gear-mechanism.jpg", tag: "Precision", title: "Gear Mechanism" },
  { img: "/defense/armoured-vehicle-spare.jpg", tag: "Armoured Vehicles", title: "Vehicle Spare" },
  { img: "/defense/defense-component.jpg", tag: "Defense", title: "Precision Component" },
];

const HEAVY = [
  { img: "/heavy/src-gearbox.png", tag: "Sinter Plant", title: "Single Roll Crusher Gearbox", big: true },
  { img: "/heavy/sinter-gearbox.png", tag: "Sinter Plant", title: "Customized Gear Box for Sinter Plant" },
  { img: "/heavy/wind-cutoff.png", tag: "Sinter Plant", title: "Wind Cut-Off Plate & Liner" },
  { img: "/heavy/four-roller-crusher.png", tag: "Sinter Plant", title: "4 Roller Crusher Assembly", big: true },
  { img: "/heavy/watercooled-roller.png", tag: "Steel Plant", title: "Water-Cooled Charging & Discharging Roller Assembly" },
  { img: "/heavy/gear-3m.png", tag: "Heavy Industry", title: "Gears Up To 2 Metre Dia" },
  { img: "/heavy/spindle.png", tag: "Heavy Industry", title: "Spindle Assembly" },
  { img: "/heavy/gearbox-industrial.png", tag: "Industrial", title: "Industrial Gear Box" },
  { img: "/heavy/gearbox-reduction.png", tag: "Industrial", title: "Reduction Gear Box" },
  { img: "/heavy/spiral-bevel.png", tag: "Gears", title: "Spiral & Straight Bevel Gears" },
  { img: "/heavy/helical.png", tag: "Gears", title: "Helical Gears" },
  { img: "/heavy/spur.png", tag: "Gears", title: "Spur Gears" },
  { img: "/heavy/worm.png", tag: "Gears", title: "Worm Wheel & Worm Shafts" },
];

const t = (n) => `/oilgas/tiles/t${n}.png`;
const OILGAS_GROUPS = [
  { group: "Bull, Handler & Reduction Gears", items: [
    { img: t(5), title: "Gear Bull 177T Spur" }, { img: t(9), title: "Gear Bull 178T Spur" },
    { img: t(22), title: "Gear Bull 75T" }, { img: t(8), title: "Gear Reducing Bull 69T" },
    { img: t(34), title: "Gear Reducing 27T/16T Spur" }, { img: t(10), title: "Gear Handler 418T" },
    { img: t(12), title: "Gear Handler 418T" }, { img: t(31), title: "Gear 376T Handler Rotate" },
  ]},
  { group: "Drive, Idler & Gearbox Gears", items: [
    { img: t(0), title: "Gear GB Input 1SP" }, { img: t(1), title: "Gear GB Output 1S" },
    { img: t(2), title: "Idler GB Right 1SP" }, { img: t(3), title: "Idler GB Left 1SP" },
  ]},
  { group: "Pinions", items: [
    { img: t(7), title: "Gear Pinion 26T Int Spline Crown" }, { img: t(14), title: "Pinion 26T Internal Taper Spur" },
    { img: t(16), title: "Pinion 28T Internal Taper Spur" }, { img: t(18), title: "Pinion LWCV Actuator" },
    { img: t(21), title: "Pinion LWCV Actuator" }, { img: t(25), title: "Pinion 76T Spur" },
    { img: t(29), title: "Pinion 19T Int Taper Spur" }, { img: t(30), title: "Pinion 60T Spur" },
    { img: t(26), title: "Gear Slewing Pinion 17T" }, { img: t(33), title: "Sun Gear 2M Machined" },
  ]},
  { group: "Shafts, Splines & Sprockets", items: [
    { img: t(4), title: "Shaft Intermediate w/ Pinion 26T" }, { img: t(17), title: "Shaft Gear / Jaw Clutch TB" },
    { img: t(23), title: "Pinion Shaft 26T" }, { img: t(24), title: "Shaft Splined 32T" },
    { img: t(6), title: "Female Drive Spline 18T" }, { img: t(11), title: "Female Drive Spline 21T" },
    { img: t(13), title: "Sprocket Double #60, 15T" },
  ]},
  { group: "Clutch, Collars & Die Blocks", items: [
    { img: t(15), title: "Piston — Jaw Clutch, Torque Boost" }, { img: t(19), title: "Single Collar Spinner Output Gear" },
    { img: t(20), title: "Double Collar Spinner Input Gear" }, { img: t(27), title: "Die Block" },
    { img: t(28), title: "Die Block" }, { img: t(32), title: "Die Block" },
  ]},
];

function OilGrid({ groups, setActive }) {
  let k = 0;
  return (
    <div className="space-y-12">
      {groups.map((grp, gi) => (
        <div key={gi}>
          <h3 className="font-head font-bold text-[#0A192F] uppercase tracked-sm text-sm mb-5 flex items-center gap-3">
            <span className="w-6 h-px gold-gradient inline-block" />{grp.group}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {grp.items.map((it, i) => {
              const idx = k++;
              return (
                <motion.button key={i} data-testid={`oilgas-item-${idx}`}
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  className="group bg-[#F1F4F8] rounded-lg overflow-hidden border border-slate-100 shadow-card text-left"
                  onClick={() => setActive({ img: "/oilgas/catalogue.png", title: "Oil & Gas — Drilling Drivetrain Catalogue" })}>
                  <div className="h-32 bg-white overflow-hidden">
                    <img src={it.img} alt={it.title} loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="px-3 py-2.5 bg-white">
                    <div className="font-head font-semibold text-[#0A192F] text-[11px] leading-snug uppercase tracked-sm">{it.title}</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Grid({ items, setActive, testprefix }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-[220px]">
      {items.map((g, i) => (
        <motion.button key={i} data-testid={`${testprefix}-${i}`} onClick={() => setActive(g)}
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
          className={`group relative rounded-xl overflow-hidden shadow-card border border-slate-100 ${g.big ? "row-span-2" : ""}`}>
          <img src={g.img} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 text-left">
            <span className="font-head text-[10px] tracked uppercase text-[#FFD700]">{g.tag}</span>
            <div className="font-head font-bold text-white uppercase tracked-sm text-sm mt-1 leading-tight">{g.title}</div>
          </div>
          <div className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ShieldCheck size={16} className="text-[#FFD700]" />
          </div>
        </motion.button>
      ))}
    </div>
  );
}

export default function DefensePage() {
  const [active, setActive] = useState(null);
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-white">
      <SiteNav />
      <PageHero eyebrow="Defense, Oil & Gas & Heavy Industry"
        title="Mission-Critical & Heavy-Industry Precision"
        sub="Aerospace gear mechanisms, M2 Bradley sprockets, naval gearboxes and gun mounts — alongside oil & gas drilling drivetrain components, sinter-plant crusher assemblies, water-cooled rollers, 2-metre gears and reverse-engineered spares." />

      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Oil & Gas · Drilling Drivetrain</span>
            <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] mt-3">Rig & Drawworks Components</h2>
            <p className="text-slate-500 mt-4 max-w-2xl leading-relaxed">Precision gears, pinions, shafts, splines and drive components manufactured for oil & gas drilling gearboxes and drawworks — grouped by part family.</p>
          </div>
          <OilGrid groups={OILGAS_GROUPS} setActive={setActive} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="mb-10">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Defense & Strategic Engineering</span>
          <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] mt-3">Mission-Critical Components</h2>
        </div>
        <Grid items={GALLERY} setActive={setActive} testprefix="defense-item" />

        <div className="mb-10 mt-24">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Heavy Industry · Steel & Sinter Plants</span>
          <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] mt-3">Gears, Gearboxes & Reverse-Engineered Spares</h2>
        </div>
        <Grid items={HEAVY} setActive={setActive} testprefix="heavy-item" />
      </section>

      <section className="bg-[#0A192F] py-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 hero-lines" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-6">
          <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-white">Have A Strategic Requirement?</h2>
          <p className="text-slate-300 mt-4">Share your drawings and specifications — our engineers respond within 2–3 business days.</p>
          <button onClick={() => nav("/", { state: { scrollTo: "quote" } })} data-testid="defense-quote-btn"
            className="btn-gold mt-8 inline-flex items-center gap-2 font-head font-bold text-sm tracked-sm uppercase px-8 py-4 rounded-lg">
            Request A Quote <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-[200] bg-[#0A192F]/95 backdrop-blur flex items-center justify-center p-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} data-testid="lightbox">
            <button className="absolute top-6 right-6 text-white hover:text-[#FFD700]" onClick={() => setActive(null)} data-testid="lightbox-close"><X size={30} /></button>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={active.img} alt={active.title} className="w-full max-h-[75vh] object-contain rounded-xl" />
              <div className="text-center mt-4">
                <span className="font-head text-xs tracked uppercase text-[#FFD700]">{active.tag}</span>
                <div className="font-head font-bold text-white uppercase tracked text-lg">{active.title}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SiteFooter />
    </div>
  );
}
